"use client";

import React, { useState } from "react";
import { TriangleAlert as AlertTriangle, ArrowRight, CircleCheck as CheckCircle2, Clock, Droplets, CircleQuestionMark as HelpCircle, PhoneCall, Pipette, ShieldAlert, Sparkles, Wrench } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
const commonIssues = [{
  id: "dripping",
  title: "Persistent Dripping",
  subtitle: "Worn washers & cartridge seals",
  description: "A steady faucet drip wastes hundreds of gallons per month and corrodes valve seats. Often caused by degraded internal O-rings, cartridge failures, or high water pressure.",
  urgency: "Moderate",
  badgeVariant: "secondary" as const
}, {
  id: "low-pressure",
  title: "Low Water Pressure",
  subtitle: "Aerator & line sediment buildup",
  description: "Mineral deposits in the aerator, crimped supply lines, or faulty diverter valves restrict water flow in kitchen sprayers and bathroom spouts.",
  urgency: "Standard",
  badgeVariant: "secondary" as const
}, {
  id: "under-sink-leak",
  title: "Leaks Under the Cabinet",
  subtitle: "P-trap loose seals & supply lines",
  description: "Standing water under sinks ruins cabinetry and causes mold. Triggered by cracked drain traps, loose slip nuts, or deteriorating shutoff valves.",
  urgency: "Urgent",
  badgeVariant: "destructive" as const
}, {
  id: "corroded-drain",
  title: "Corroded Sink Basins & Drains",
  subtitle: "Rusting strainers & degraded putty",
  description: "Deteriorated plumber's putty and rusted basket strainers break the watertight basin barrier, causing slow seeps whenever the sink is filled.",
  urgency: "Standard",
  badgeVariant: "secondary" as const
}];
const repairSolutions = [{
  title: "Cartridge & Stem Replacement",
  description: "Precision rebuild of single-handle and two-handle faucets using OEM parts to eliminate leaks without replacing the fixture.",
  points: ["Exact OEM ceramic disk cartridges", "New valve seats and O-ring replacements", "Smooth handle operation restored"]
}, {
  title: "P-Trap & Drain Assembly Overhaul",
  description: "Replacement of fragile or corroded under-sink pipes with heavy-gauge PVC or brass drain assemblies.",
  points: ["Slip-joint seal reinforcement", "New stainless basket strainers", "Tight watertight testing under full flow"]
}, {
  title: "New Fixture Installation",
  description: "Complete mounting of modern kitchen pull-down sprays, touchless faucets, undermount sinks, and vanity basins.",
  points: ["Braided stainless water supply lines", "Level silicone perimeter sealing", "Integrated garbage disposal connection"]
}];
const faqItems = [{
  question: "When should I repair vs replace my faucet?",
  answer: "If the faucet body has internal hairline cracks, severe exterior corrosion, or is over 15 years old, replacement is typically more cost-effective. If it is simply a leak from the spout or handle, replacing the internal cartridge or stem resolves the issue quickly."
}, {
  question: "What causes water to puddle under the kitchen sink?",
  answer: "The most frequent culprits are loose slip nuts on the P-trap, worn plumber's putty around the basket strainer, leaking pull-out spray hoses, or failed supply shutoff valves. We diagnose the exact leak point within minutes."
}, {
  question: "How fast can you arrive for an active emergency leak?",
  answer: "Our Jonesboro emergency technicians are available 24/7. In active flood situations, our dispatchers guide you to shut off your local isolation valve while our team heads to your property."
}, {
  question: "Do you provide sink crack repairs?",
  answer: "Hairline cracks in porcelain or acrylic sinks can sometimes be sealed, but structural basin cracks that compromise mounting or water retention require replacement to protect your counters and subfloors."
}];
export default function FaucetRepair() {
  const [activeTab, setActiveTab] = useState("issues");
  const shouldReduceMotion = useReducedMotion();
  return <section id="faucetandsinkrepair" data-nav="light" className="w-full bg-background text-foreground py-16 md:py-24 border-b border-border transition-colors">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
        {/* Header Block */}
        <div className="flex flex-col items-center text-center max-w-3xl mx-auto space-y-4">
          <Badge variant="secondary" className="px-3 py-1 font-medium">
            <Wrench className="size-3.5 mr-1 text-primary" />
            Fixture Diagnostics & Repairs
          </Badge>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-default font-bold tracking-tight text-foreground">
            Faucet &amp; Sink Repair Services
          </h2>
          <p className="text-muted-foreground text-base md:text-lg leading-relaxed font-sans">
            Dripping taps, loose vanity drains, and under-cabinet moisture can
            waste water and rot wood quickly. Jonesboro Emergency Plumber
            provides immediate diagnostic inspections, precision rebuilds, and
            full sink replacements.
          </p>
        </div>

        {/* Emergency Notice Ribbon */}
        <div className="mt-10 bg-secondary rounded-xl p-4 md:p-6 border border-border flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-primary text-primary-foreground rounded-lg shrink-0">
              <ShieldAlert className="size-6" />
            </div>
            <div>
              <h3 className="font-default font-semibold text-foreground text-base md:text-lg">
                Active Leak Under Your Sink?
              </h3>
              <p className="text-sm text-muted-foreground">
                Shut off the dual angle stops beneath the basin and contact our
                24/7 Jonesboro technicians immediately.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 w-full md:w-auto">
            <Button asChild size="lg" className="w-full md:w-auto">
              <a href="tel:4708479076">
                <PhoneCall className="size-4" />
                Call 24/7 Dispatch
              </a>
            </Button>
          </div>
        </div>

        {/* Tabbed Interactive Section */}
        <div className="mt-14">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full space-y-8">
            <div className="flex justify-center">
              <TabsList className="grid grid-cols-2 max-w-md w-full h-11">
                <TabsTrigger value="issues" className="text-sm font-medium">
                  Common Problems
                </TabsTrigger>
                <TabsTrigger value="solutions" className="text-sm font-medium">
                  Repair Solutions
                </TabsTrigger>
              </TabsList>
            </div>

            {/* TAB: Common Issues */}
            <TabsContent value="issues" className="space-y-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {commonIssues.map((issue, i) => <motion.div key={issue.id} data-index={i} initial={shouldReduceMotion ? {
                opacity: 1
              } : {
                opacity: 0,
                y: 15
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.35,
                delay: i * 0.08
              }}>
                    <Card className="h-full border border-border bg-card shadow-sm hover:shadow-md transition-shadow">
                      <CardHeader className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="p-2 rounded-md bg-muted text-foreground">
                            {issue.id === "under-sink-leak" ? <AlertTriangle className="size-5 text-destructive" /> : <Droplets className="size-5 text-primary" />}
                          </span>
                          <Badge variant={issue.badgeVariant}>
                            {issue.urgency}
                          </Badge>
                        </div>
                        <CardTitle className="text-xl font-bold font-default text-card-foreground">
                          {issue.title}
                        </CardTitle>
                        <CardDescription className="text-sm font-medium text-muted-foreground">
                          {issue.subtitle}
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-card-foreground/80 leading-relaxed">
                          {issue.description}
                        </p>
                      </CardContent>
                    </Card>
                  </motion.div>)}
              </div>
            </TabsContent>

            {/* TAB: Repair Solutions */}
            <TabsContent value="solutions" className="space-y-8">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {repairSolutions.map((solution, i) => <motion.div key={solution.title} data-index={i} initial={shouldReduceMotion ? {
                opacity: 1
              } : {
                opacity: 0,
                y: 15
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.35,
                delay: i * 0.08
              }}>
                    <Card className="h-full flex flex-col justify-between border border-border bg-card shadow-sm">
                      <CardHeader className="space-y-2">
                        <div className="size-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                          <Sparkles className="size-5 text-primary" />
                        </div>
                        <CardTitle className="text-lg font-bold font-default text-card-foreground">
                          {solution.title}
                        </CardTitle>
                        <CardDescription className="text-sm text-muted-foreground">
                          {solution.description}
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-2.5 pt-2">
                        {solution.points.map((pt, pIdx) => <div key={pIdx} data-index={pIdx} className="flex items-start gap-2 text-sm text-card-foreground">
                            <CheckCircle2 className="size-4 text-accent shrink-0 mt-0.5" />
                            <span>{pt}</span>
                          </div>)}
                      </CardContent>
                    </Card>
                  </motion.div>)}
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* Feature Visual Grid */}
        <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center bg-card rounded-2xl border border-border p-6 md:p-10">
          <div className="lg:col-span-5 space-y-6">
            <Badge variant="outline" className="text-foreground">
              Jonesboro Standard
            </Badge>
            <h3 className="text-2xl md:text-3xl font-default font-bold text-card-foreground leading-tight">
              Quality Fixture Repairs Built to Stop Recurrent Leaks
            </h3>
            <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
              We carry high-wear replacement cartridges, brass valves, and heavy-duty
              tailpieces on every service vehicle to complete most faucet and
              sink repairs on the initial visit.
            </p>
            <div className="space-y-3">
              <div className="flex items-center gap-3 text-sm font-medium text-card-foreground">
                <Clock className="size-4 text-primary shrink-0" />
                <span>Same-day appointment availability across Clayton County</span>
              </div>
              <div className="flex items-center gap-3 text-sm font-medium text-card-foreground">
                <Pipette className="size-4 text-primary shrink-0" />
                <span>Complete water line leak checks and pressure tests</span>
              </div>
              <div className="flex items-center gap-3 text-sm font-medium text-card-foreground">
                <CheckCircle2 className="size-4 text-primary shrink-0" />
                <span>Guaranteed workmanship on all parts and labor</span>
              </div>
            </div>
            <div className="pt-2 flex flex-wrap gap-3">
              <Button asChild variant="default">
                <Link to="/contact/">
                  Book Sink Inspection
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/services/">
                  All Plumbing Services
                </Link>
              </Button>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="relative rounded-xl overflow-hidden border border-border aspect-video shadow-md">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/sink_faucet_repair_technician.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/sink_faucet_repair_technician.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/sink_faucet_repair_technician.png-wvc-sizes" alt="Plumber repairing a kitchen sink faucet with specialized tools" loading="lazy" decoding="async" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>

        {/* FAQs Section */}
        <div className="mt-16 max-w-3xl mx-auto space-y-6">
          <div className="text-center space-y-2">
            <div className="inline-flex p-2 bg-muted rounded-full text-foreground mb-1">
              <HelpCircle className="size-5" />
            </div>
            <h3 className="text-2xl font-default font-bold text-foreground">
              Frequently Asked Questions
            </h3>
            <p className="text-sm text-muted-foreground">
              Straightforward guidance on keeping your sinks and faucets leak-free.
            </p>
          </div>

          <Accordion type="single" collapsible className="w-full">
            {faqItems.map((faq, i) => <AccordionItem key={i} value={`faq-${i}`} data-index={i}>
                <AccordionTrigger className="text-left font-semibold text-foreground">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-sm leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>)}
          </Accordion>
        </div>
      </div>
    </section>;
}